import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Users, MessageCircle, Inbox, Home, CreditCard, ShieldAlert, Heart, Zap, TrendingUp, BadgeCheck, Flag } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const KPI_CONF = [
    { key: "users.total", label: "Total users", icon: Users, color: "#E85D5D" },
    { key: "users.dau", label: "DAU today", icon: Zap, color: "#F59E0B" },
    { key: "users.new_today", label: "New today", icon: TrendingUp, color: "#10B981" },
    { key: "users.new_week", label: "New this week", icon: TrendingUp, color: "#10B981" },
    { key: "users.verified", label: "Verified ✓", icon: ShieldAlert, color: "#3B82F6" },
    { key: "users.premium", label: "Premium 💎", icon: CreditCard, color: "#A855F7" },
    { key: "users.banned", label: "Banned", icon: ShieldAlert, color: "#EF4444" },
    { key: "engagement.matches", label: "Total matches", icon: Heart, color: "#EC4899" },
    { key: "engagement.messages", label: "Messages sent", icon: MessageCircle, color: "#06B6D4" },
    { key: "engagement.flagged_messages", label: "Flagged msgs", icon: ShieldAlert, color: "#F97316" },
    { key: "verifications.pending", label: "Verifs pending", icon: BadgeCheck, color: "#E85D5D" },
    { key: "reports.open", label: "Open reports", icon: Flag, color: "#EF4444" },
    { key: "waitlist.total", label: "Waitlist", icon: Inbox, color: "#84CC16" },
    { key: "waitlist.new_today", label: "Waitlist today", icon: Inbox, color: "#84CC16" },
    { key: "listings.total", label: "Total listings", icon: Home, color: "#8B5CF6" },
    { key: "listings.active", label: "Active listings", icon: Home, color: "#8B5CF6" },
    { key: "revenue.month_inr", label: "Revenue (30d)", icon: CreditCard, color: "#22C55E", prefix: "₹" },
    { key: "revenue.total_inr", label: "Revenue (total)", icon: CreditCard, color: "#22C55E", prefix: "₹" },
];

function pick(obj, path) {
    return path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
}

export default function AdminOverview() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [trends, setTrends] = useState(null);
    const [range, setRange] = useState(30);

    useEffect(() => {
        (async () => {
            try {
                const r = await adminApi.get("/admin/overview");
                setData(r.data);
            } catch (e) {}
            setLoading(false);
        })();
    }, []);

    useEffect(() => {
        adminApi.get("/admin/trends", { params: { days: range } })
            .then((r) => setTrends(r.data))
            .catch(() => {});
    }, [range]);

    return (
        <div data-testid="admin-overview-page">
            <h1 className="font-display text-3xl font-black">Overview</h1>
            <p className="text-sm text-white/50 mt-1">Real-time snapshot of Flatch.</p>

            {loading ? (
                <div className="mt-6 text-white/50 text-sm">Loading…</div>
            ) : (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {KPI_CONF.map((k) => {
                        const val = pick(data, k.key) ?? 0;
                        const Icon = k.icon;
                        return (
                            <div
                                key={k.key}
                                data-testid={`kpi-${k.key.replace(".", "-")}`}
                                className="rounded-2xl bg-white/5 border border-white/10 p-4"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="text-[10px] font-black uppercase tracking-widest text-white/50">
                                        {k.label}
                                    </div>
                                    <div className="h-7 w-7 rounded-lg grid place-items-center" style={{ background: `${k.color}30`, color: k.color }}>
                                        <Icon className="h-3.5 w-3.5" strokeWidth={2.8} />
                                    </div>
                                </div>
                                <div className="mt-2 font-display text-3xl font-black tracking-tight">
                                    {k.prefix || ""}{typeof val === "number" ? val.toLocaleString() : val}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Trend charts */}
            <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-display text-xl font-black flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-[#E85D5D]" /> Growth trends
                </h2>
                <div className="inline-flex rounded-xl border border-white/10 bg-white/5 p-1" data-testid="trends-range">
                    {[7, 30, 90].map((d) => (
                        <button
                            key={d}
                            onClick={() => setRange(d)}
                            data-testid={`trends-range-${d}`}
                            className={`px-3 py-1 rounded-lg text-xs font-bold transition ${range === d ? "bg-[#E85D5D] text-white" : "text-white/60 hover:text-white"}`}
                        >
                            {d}d
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="admin-trends">
                <TrendChart title="Signups" dataKey="signups" color="#E85D5D" series={trends?.series} total={trends?.totals?.signups} />
                <TrendChart title="Matches" dataKey="matches" color="#EC4899" series={trends?.series} total={trends?.totals?.matches} />
                <TrendChart title="Revenue" dataKey="revenue_inr" color="#22C55E" series={trends?.series} total={trends?.totals?.revenue_inr} prefix="₹" />
            </div>
        </div>
    );
}

function TrendChart({ title, dataKey, color, series, total, prefix = "" }) {
    const gid = `grad-${dataKey}`;
    return (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid={`trend-${dataKey}`}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-black uppercase tracking-widest text-white/50">{title}</div>
                <div className="font-display text-lg font-black" style={{ color }}>
                    {prefix}{(total ?? 0).toLocaleString()}
                </div>
            </div>
            <div className="mt-3 h-40">
                {!series ? (
                    <div className="h-full grid place-items-center text-white/30 text-xs">Loading…</div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={series} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(d) => (d || "").slice(5)} minTickGap={24} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.4)" }} width={28} allowDecimals={false} axisLine={false} tickLine={false} />
                            <Tooltip
                                contentStyle={{ background: "#14141A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }}
                                labelStyle={{ color: "rgba(255,255,255,0.6)" }}
                                formatter={(v) => [`${prefix}${v}`, title]}
                            />
                            <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={`url(#${gid})`} />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}
